import React, { useState, useEffect } from 'react';
import { performanceMonitor } from '../utils/performance';
import { logger } from '../utils/logger';
import { isDevelopment } from '../config/env';

interface PerformanceStats {
  navigation?: {
    count: number;
    avg: number;
    max: number;
    min: number;
  };
  apiCalls?: {
    count: number;
    avg: number;
    max: number;
    min: number;
  };
  renders?: {
    count: number;
    avg: number;
    max: number;
    min: number;
  };
}

export function PerformanceDashboard() {
  const [isOpen, setIsOpen] = useState(false);
  const [stats, setStats] = useState<PerformanceStats>({});
  const [memoryUsage, setMemoryUsage] = useState<any>(null);

  useEffect(() => {
    if (!isDevelopment) return;

    const updateStats = () => {
      const summary = performanceMonitor.getMetricsSummary();
      
      setStats({
        navigation: summary.page_load_time,
        apiCalls: summary.api_call_duration,
        renders: summary.function_search_filtering
      });

      // Get memory usage if available
      if ('memory' in performance) {
        const memory = (performance as any).memory;
        setMemoryUsage({
          used: Math.round(memory.usedJSHeapSize / 1024 / 1024),
          total: Math.round(memory.totalJSHeapSize / 1024 / 1024),
          limit: Math.round(memory.jsHeapSizeLimit / 1024 / 1024),
          percentage: Math.round((memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100)
        });
      }
    };

    updateStats();
    const interval = setInterval(updateStats, 5000); // Update every 5 seconds

    return () => clearInterval(interval);
  }, []);

  const handleClearMetrics = () => {
    performanceMonitor.cleanup();
    setStats({});
    logger.info('Performance metrics cleared', { component: 'PerformanceDashboard' });
  };

  const handleExportData = () => {
    const metrics = performanceMonitor.getMetrics();
    const dataStr = JSON.stringify({ stats, memoryUsage, metrics }, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `naia-performance-${Date.now()}.json`;
    link.click();
    
    URL.revokeObjectURL(url);
    logger.info('Performance data exported', { component: 'PerformanceDashboard' });
  };

  if (!isDevelopment) return null;

  return (
    <div className="fixed bottom-4 left-4 z-50">
      {/* Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="bg-naia-pink text-white p-2 rounded-full shadow-lg hover:bg-soft-violet transition-colors"
        title="Performance Dashboard"
      >
        📊
      </button>

      {/* Dashboard Panel */}
      {isOpen && (
        <div className="absolute bottom-12 left-0 bg-dark-void/95 backdrop-blur-sm border border-white/20 rounded-lg p-4 w-80 shadow-xl">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-pearl-white">Performance</h3>
            <div className="flex gap-2">
              <button
                onClick={handleExportData}
                className="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                title="Export data"
              >
                Export
              </button>
              <button
                onClick={handleClearMetrics}
                className="text-xs px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700"
                title="Clear metrics"
              >
                Clear
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="text-xs px-2 py-1 bg-gray-600 text-white rounded hover:bg-gray-700"
              >
                ✕
              </button>
            </div>
          </div>

          <div className="space-y-3 text-xs">
            {/* Memory Usage */}
            {memoryUsage && (
              <div className="bg-white/5 rounded p-2">
                <div className="text-pearl-white font-medium mb-1">Memory Usage</div>
                <div className="text-slate-grey">
                  {memoryUsage.used}MB / {memoryUsage.limit}MB ({memoryUsage.percentage}%)
                </div>
                <div className="w-full bg-gray-700 rounded-full h-1 mt-1">
                  <div 
                    className="bg-naia-pink h-1 rounded-full" 
                    style={{ width: `${Math.min(memoryUsage.percentage, 100)}%` }}
                  />
                </div>
              </div>
            )}

            {/* Page Load Time */}
            {stats.navigation && (
              <div className="bg-white/5 rounded p-2">
                <div className="text-pearl-white font-medium mb-1">Page Load</div>
                <div className="text-slate-grey">
                  Avg: {Math.round(stats.navigation.avg)}ms
                  <br />
                  Max: {Math.round(stats.navigation.max)}ms
                  <br />
                  Count: {stats.navigation.count}
                </div>
              </div>
            )}

            {/* API Calls */}
            {stats.apiCalls && (
              <div className="bg-white/5 rounded p-2">
                <div className="text-pearl-white font-medium mb-1">API Calls</div>
                <div className="text-slate-grey">
                  Avg: {Math.round(stats.apiCalls.avg)}ms
                  <br />
                  Max: {Math.round(stats.apiCalls.max)}ms
                  <br />
                  Count: {stats.apiCalls.count}
                </div>
              </div>
            )}

            {/* Search Performance */}
            {stats.renders && (
              <div className="bg-white/5 rounded p-2">
                <div className="text-pearl-white font-medium mb-1">Search</div>
                <div className="text-slate-grey">
                  Avg: {Math.round(stats.renders.avg)}ms
                  <br />
                  Max: {Math.round(stats.renders.max)}ms
                  <br />
                  Count: {stats.renders.count}
                </div>
              </div>
            )}

            {Object.keys(stats).length === 0 && (
              <div className="text-slate-grey text-center py-4">
                No performance data yet.
                <br />
                Use the app to collect metrics.
              </div>
            )}
          </div>

          <div className="mt-3 pt-3 border-t border-white/10 text-xs text-slate-grey">
            💡 Access detailed metrics via console:
            <br />
            <code className="text-naia-pink">__NAIA_DEVTOOLS__.utils.getPerformanceReport()</code>
          </div>
        </div>
      )}
    </div>
  );
} 